import { normalize } from 'normalizr';
import { createReducer } from '@reduxjs/toolkit';
import { find, each } from 'underscore';

import {
  fetchComments,
  createComment,
  deleteComment,
  updateComment,
  voteUpComment,
  voteDownComment,
  addComment,
  editComment,
  removeComment,
  voteComment,
  getCommentLikersInfo,
  removeNewCommentId,
} from 'redux/actions/comments';

import {
  FetchCommentsNormalizedResult,
  FetchCommentsEntities,
  FetchCommentsSchema,
} from 'redux/schemas/api/comment';
import { DiscussionMember, Post } from 'redux/schemas/models/post';

import { initialRootState } from '.';

/**
 * Find member by user id
 * @param {DiscussionMember[]} members the list of members to search through
 * @param {number} userId id to find user of member
 * @returns {number} the index of the member
 */
export const findMemberByUserId = (members: DiscussionMember[], userId: number) => find(members, m => m.user.id === userId);

const parseNewComment = (state, comment, postId) => {
  const post = state.models.posts[postId];
  if (!post) return;

  if (state.models.comments[comment.id]) return;

  post.commentIds.push(comment.id);

  Object.assign(state.models.comments, {
    [comment.id]: {
      ...comment,
      likers: [],
    },
  });

  if (comment.isFirstContribution) {
    state.app.newCommentIdsForCurrentUser.push(comment.id);
  }
};

const parseDeleteComment = (state, commentId, postId) => {
  const post = state.models.posts[postId];
  if (!post) return;

  if (!state.models.comments[commentId]) return;

  post.commentIds = post.commentIds.filter(id => id !== commentId);
  delete state.models.comments[commentId];
};

const parseVoteComment = (state, commentId, numLikes) => {
  Object.assign(state.models.comments[commentId], {
    votesCount: numLikes,
  });
};

export default createReducer(initialRootState, builder => {
  builder
    .addCase(removeNewCommentId, (state, action) => {
      state.app.newCommentIdsForCurrentUser = state.app.newCommentIdsForCurrentUser.filter(id => action.payload.commentId !== id);
    })
    .addCase(fetchComments.fulfilled, (state, action) => {
      const {
        entities: { comments },
        result: { 
          posts,
          additionalPostsBeforeCount,
          additionalPostsAfterCount,
          additionalNewPostsBeforeCount,
          additionalNewPostsAfterCount,
        },
      } = normalize<FetchCommentsNormalizedResult, FetchCommentsEntities>(action.payload, FetchCommentsSchema);

      const { postId, beforeId, afterId } = action.meta.arg;
      const post = state.models.posts[postId];

      let commentIds = [...(post.commentIds ?? [])];
      if (beforeId) {
        commentIds = [...posts, ...commentIds];
      } else if (afterId) {
        commentIds = [...commentIds, ...posts];
      } else {
        commentIds = [...posts];
      }

      Object.assign(post, {
        commentIds,
        additionalCommentsAfterCount: additionalPostsAfterCount,
        additionalCommentsBeforeCount: additionalPostsBeforeCount,
        additionalNewCommentsBeforeCount: additionalNewPostsBeforeCount,
        additionalNewCommentsAfterCount: additionalNewPostsAfterCount,
        commentsFetched: true,
      });

      Object.assign(state.models.comments, comments);
    })
    .addCase(getCommentLikersInfo.fulfilled, (state, action) => {
      const { commentId } = action.meta.arg;
      const comment = state.models.comments[commentId];
      comment.likers = action.payload;
      comment.likersFetched = true;
    })
    .addCase(addComment.fulfilled, (state, action) => {
      const { comment, postId } = action.payload;
      parseNewComment(state, comment, postId);
    })
    .addCase(createComment.fulfilled, (state, action) => {
      const comment = action.payload;
      const { postId } = action.meta.arg;
      parseNewComment(state, comment, postId);
    })
    .addCase(removeComment.fulfilled, (state, action) => {
      const { commentId, postId, userId } = action.payload;
      parseDeleteComment(state, commentId, postId);
    })
    .addCase(deleteComment.fulfilled, (state, action) => {
      const { commentId, postId } = action.meta.arg;
      parseDeleteComment(state, commentId, postId);
    })
    .addCase(editComment.fulfilled, (state, action) => {
      const { comment } = action.payload;
      Object.assign(state.models.comments[comment.id], comment);
    })
    .addCase(updateComment.fulfilled, (state, action) => {
      const { commentId } = action.meta.arg;
      Object.assign(state.models.comments[commentId], action.payload);
    })
    .addCase(voteComment.fulfilled, (state, action) => {
      const { commentId, numLikes } = action.payload;
      parseVoteComment(state, commentId, numLikes);
    })
    .addCase(voteUpComment.fulfilled, (state, action) => {
      const { commentId } = action.meta.arg;
      const { numLikes } = action.payload;

      Object.assign(state.models.comments[commentId], {
        votesCount: numLikes,
        liked: true,
      });
    })
    .addCase(voteDownComment.fulfilled, (state, action) => {
      const { commentId } = action.meta.arg;
      const { numLikes } = action.payload;

      Object.assign(state.models.comments[commentId], {
        votesCount: numLikes,
        liked: false,
      });
    });
});
